import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import PropTypes from 'prop-types';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import mCustomer from 'models/customer';
// Custom
import {
  openDialog, closeDialog,
  getInstallationCustomersList,
  updateInstallationCustomerState, deleteInstallationCustomer,
  generateInstallationCustomersDailyCodes,
  triggerInstallationCustomerAccesswayRight,
  refreshList,
} from 'actions';
import { fullListShape, deleteShape } from 'constants/PropTypesShapes';
import { computeErrors } from 'utils/Forms';
import {
  customersAddPath, navigateToCustomersShow, planningsPath,
} from 'installations/navigation';
import {
  AddTextButton, DailyCodesTextButton, PlanningsTextButton,
} from 'components/buttons/TextButtons';
import SubListTemplate from 'components/templates/SubListTemplate';
import withPermissions from 'hocs/WithPermissions';
import withConfirmDialog from 'hocs/WithConfirmDialog';
import withMessageDialog from 'hocs/WithMessageDialog';
import withEntitiesInformation from 'installations/assets/hocs/WithEntitiesInformation';
import withResources from 'installations/assets/hocs/WithResources';
import withStateReinit from 'hocs/WithStateReinit';
import CustomerFilters from 'installations/assets/customers/CustomerFilters';
import CustomerRow from 'installations/assets/customers/CustomerRow';
import CustomerRowDetails from 'installations/assets/customers/CustomerRowDetails';
import { OpacInstallationContext } from 'components/misc/Contexts';

class CustomersList extends React.Component {
  constructor(props) {
    super(props);

    this.handleDeleteCustomer = this.handleDeleteCustomer.bind(this);
    this.handleTriggerAccessWayRight = this.handleTriggerAccessWayRight.bind(this);
    this.handleUpdateState = this.handleUpdateState.bind(this);
    this.handleReinitState = this.handleReinitState.bind(this);
    this.handleDisplaySyncHistory = this.handleDisplaySyncHistory.bind(this);
    this.handleGenerateDailyCodes = this.handleGenerateDailyCodes.bind(this);
    this.handleValidateDailyCodes = this.handleValidateDailyCodes.bind(this);
    this.handleRowDbleClick = this.handleRowDbleClick.bind(this);
    this.doGetRowComp = this.doGetRowComp.bind(this);
    this.doGetRowCompDetails = this.doGetRowCompDetails.bind(this);
  }

  componentDidUpdate(prevProps) {
    const {
      customerDelete, stateUpdated, dailyCodesGenerated, exportDone,
      handleDisplayMessage, handleRefreshList, handleCloseDialog,
    } = this.props;

    // Operations catching
    if (!prevProps.dailyCodesGenerated && dailyCodesGenerated) {
      // Close daily codes popup
      handleCloseDialog('INSTALLATION_DAILY_CODES_ADD');
    }
    if (
      (!prevProps.customerDelete.deleted && customerDelete.deleted)
      || (!prevProps.stateUpdated && stateUpdated)
      || (!prevProps.dailyCodesGenerated && dailyCodesGenerated)
    ) {
      handleRefreshList();
    }

    // Check if export is done
    if (!prevProps.exportDone && exportDone) {
      handleDisplayMessage('info.fileCreationLinkEmail', {}, 'info');
    }

    // Check errors
    if (customerDelete.error && !prevProps.customerDelete.error) {
      // Compute errors
      const errors = computeErrors(null, customerDelete.error);
      // Display message
      handleDisplayMessage(`error.${errors.global}`, {}, 'warning');
    }
  }

  // Handlers
  handleDeleteCustomer(customer) {
    const { params, handleDisplayConfirm, handleDeleteCustomer } = this.props;
    handleDisplayConfirm(
      { id: 'confirm.deleteCustomer', values: { customerName: mCustomer.fullName(customer) } },
      () => handleDeleteCustomer(params.id, customer.id),
    );
  }

  handleTriggerAccessWayRight(customer, accessWay, right) {
    const { params, handleTriggerRight } = this.props;
    // Trigger the right
    handleTriggerRight(params.id, customer.id, accessWay.id, right.id, mCustomer.fullName(customer));
  }

  handleUpdateState(customer, data) {
    const { params, handleUpdateCustomerState } = this.props;
    handleUpdateCustomerState(
      params.id,
      customer.id,
      mCustomer.serializeState({
        customer,
        ...data,
      }),
    );
  }

  handleReinitState(customer) {
    const { params, handleDisplayConfirm, handleUpdateCustomerState } = this.props;
    handleDisplayConfirm(
      { id: 'confirm.reinitStateCustomer' },
      () => {
        handleUpdateCustomerState(
          params.id,
          customer.id,
          mCustomer.serializeState({
            customer,
            insideQuantity: 0,
            outsideQuantity: 0,
          }),
        );
      },
      { confirmLabel: 'confirm' },
    );
  }

  handleDisplaySyncHistory(customer) {
    const { params, handleOpenDialog } = this.props;

    handleOpenDialog({
      title: {
        id: 'title.asyncOperationsResourceHistory',
      },
      actions: [
        { type: 'close' },
      ],
      component: {
        type: 'INSTALLATION_ASYNC_OPERATION_HISTORY',
        props: {
          id: params.id,
          asyncOperation: {
            id: customer.async_operation_id,
            resource_id: customer.id,
            resource_type: 'customer',
          },
        },
      },
      size: 'md',
    });
  }

  handleGenerateDailyCodes(e) {
    e.preventDefault();
    const { handleOpenDialog } = this.props;
    const { timezone } = this.context;
    handleOpenDialog({
      title: {
        id: 'title.generateDailyCodes',
      },
      actions: [
        { type: 'cancel' },
        { type: 'validate', preventClosing: true },
      ],
      component: {
        type: 'INSTALLATION_DAILY_CODES_ADD',
        props: {
          onValidate: this.handleValidateDailyCodes,
          timezone,
        },
      },
    });
  }

  handleValidateDailyCodes(data) {
    const { params, handleGenerateCustomersDailyCodes } = this.props;
    handleGenerateCustomersDailyCodes(params.id, mCustomer.serializeDailyCodes(data));
  }

  handleRowDbleClick(customer) {
    const { params, hasPermission } = this.props;
    const { pInstallation: { customerWrite } } = permissions;
    if (customer && hasPermission({ needed: customerWrite, permissionsOrContext: 'installationPermissions' })) {
      // Display customer
      navigateToCustomersShow(params.id, customer.id);
    }
  }

  // Private
  doGetRowComp(props) {
    const {
      params, antipassbackEnabled, hasMultipleEntities, hasHostOpBookingsEntity,
    } = this.props;
    return (
      <CustomerRow
        {...props}
        id={params.id}
        antipassbackEnabled={antipassbackEnabled}
        withEntity={hasMultipleEntities}
        withOneParkBooking={hasHostOpBookingsEntity}
        onDelete={this.handleDeleteCustomer}
      />
    );
  }

  doGetRowCompDetails(props) {
    const { params } = this.props;
    return (
      <CustomerRowDetails
        {...props}
        id={params.id}
        onDisplaySyncHistory={this.handleDisplaySyncHistory}
        onTriggerRight={this.handleTriggerAccessWayRight}
        onUpdateState={this.handleUpdateState}
        onReinitState={this.handleReinitState}
      />
    );
  }

  render() {
    const {
      antipassbackEnabled, customers, params, hasPermission, desynchronized,
      hasMultipleEntities, handleGetCustomersList, hasHostOpBookingsEntity,
    } = this.props;

    // Permissions
    const {
      pInstallation: {
        customerWrite, configRead, entityRead, planningRead,
      },
    } = permissions;

    // Extra actions Buttons
    const extraActionButtons = [];
    if (hasPermission({ needed: planningRead, permissionsOrContext: 'installationPermissions' })) {
      extraActionButtons.push(<PlanningsTextButton key="bPla" primary path={planningsPath(params.id)} />);
    }

    // No entity filter if user has not multiple entities
    const excludedFilterFields = [];
    if (!hasMultipleEntities) {
      excludedFilterFields.push('entityId');
    }

    const { timezone } = this.context;

    return (
      <SubListTemplate
        title={<LocalizedMessage id="title.customersList" />}
        displayRefresh
        actionButtons={[
          <OnlyWith
            key="bAdd"
            permissions={{
              needed: `${customerWrite}&&${configRead}&&${entityRead}`,
              permissionsOrContext: 'installationPermissions',
            }}
          >
            <AddTextButton primary path={customersAddPath(params.id)} />
          </OnlyWith>,
          <OnlyWith
            key="bCod"
            permissions={{
              needed: customerWrite,
              permissionsOrContext: 'installationPermissions',
            }}
          >
            <DailyCodesTextButton primary onClick={this.handleGenerateDailyCodes} />
          </OnlyWith>,
        ]}
        extraActionButtons={extraActionButtons}
        name={mCustomer.name}
        listFeatures={mCustomer.listFeatures({
          withEntity: hasMultipleEntities,
          antipassbackEnabled,
          withOneParkBooking: hasHostOpBookingsEntity,
        })}
        withActions={hasPermission({ needed: customerWrite, permissionsOrContext: 'installationPermissions' })}
        nbActions={2}
        withDetails
        elementsData={{
          loading: customers.loading,
          desynchronized,
          elements: customers.elements,
          handleGetList: (pagination, sort, filters, firstLoad) => handleGetCustomersList(params.id, pagination, sort, filters, firstLoad, desynchronized),
        }}
        onRowDbleClick={this.handleRowDbleClick}
        excludedFilterFields={excludedFilterFields}
        filtersComp={<CustomerFilters id={params.id} timezone={timezone} />}
        rowComp={this.doGetRowComp}
        rowCompDetails={this.doGetRowCompDetails}
      />
    );
  }
}

CustomersList.contextType = OpacInstallationContext;

CustomersList.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  antipassbackEnabled: PropTypes.bool.isRequired,
  customers: fullListShape.isRequired,
  customerDelete: deleteShape.isRequired,
  stateUpdated: PropTypes.bool.isRequired,
  dailyCodesGenerated: PropTypes.bool.isRequired,
  exportDone: PropTypes.bool.isRequired,
  desynchronized: PropTypes.bool.isRequired,
  hasPermission: PropTypes.func.isRequired,
  hasMultipleEntities: PropTypes.bool.isRequired,
  hasHostOpBookingsEntity: PropTypes.bool.isRequired,
  handleDisplayMessage: PropTypes.func.isRequired,
  handleRefreshList: PropTypes.func.isRequired,
  handleOpenDialog: PropTypes.func.isRequired,
  handleCloseDialog: PropTypes.func.isRequired,
  handleDisplayConfirm: PropTypes.func.isRequired,
  handleGetCustomersList: PropTypes.func.isRequired,
  handleUpdateCustomerState: PropTypes.func.isRequired,
  handleDeleteCustomer: PropTypes.func.isRequired,
  handleGenerateCustomersDailyCodes: PropTypes.func.isRequired,
  handleTriggerRight: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  antipassbackEnabled:
    state.installation.current.data.antipassback.enabled_entrance
    || state.installation.current.data.antipassback.enabled_exit,
  customers: state.installationCustomer.list,
  customerDelete: state.installationCustomer.delete,
  stateUpdated: state.installationCustomer.updateState.updated,
  dailyCodesGenerated: state.installationCustomer.dailyCodes.done,
  exportDone: state.installationCustomer.export.done,
  desynchronized: state.installationCustomer.list.desynchronized,
});

const mapDispatchToProps = (dispatch) => ({
  handleRefreshList: () => {
    dispatch(refreshList());
  },
  handleOpenDialog: (props) => {
    dispatch(openDialog(props));
  },
  handleCloseDialog: (componentType) => {
    dispatch(closeDialog(componentType));
  },
  handleGetCustomersList: (id, pagination, sort, filters, firstLoad, desynchronized) => {
    dispatch(getInstallationCustomersList(id, pagination, sort, filters, firstLoad, desynchronized));
  },
  handleUpdateCustomerState: (id, idCustomer, data) => {
    dispatch(updateInstallationCustomerState(id, idCustomer, data));
  },
  handleDeleteCustomer: (id, idCustomer) => {
    dispatch(deleteInstallationCustomer(id, idCustomer));
  },
  handleGenerateCustomersDailyCodes: (id, data) => {
    dispatch(generateInstallationCustomersDailyCodes(id, data));
  },
  handleTriggerRight: (id, idCustomer, idAccessWay, idRight, name) => {
    dispatch(triggerInstallationCustomerAccesswayRight(id, idCustomer, idAccessWay, idRight, name));
  },
});

module.exports = compose(
  withResources(
    (ownProps) => ({
      id: ownProps.params.id,
    }),
    ['devices', 'scenarios', 'plannings'],
  ),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withPermissions,
  withConfirmDialog,
  withMessageDialog,
  withEntitiesInformation,
  withStateReinit(['deleteInstallationCustomer']),
)(CustomersList);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/routes/show/routes/customers/routes/index/components/InstallationCustomersList.js